/**
 * 문서 편집기가 여는 확장자 정본 — 백엔드 {@code OfficeFormats.java} 와 <b>같은 표</b>다.
 *
 * <p>도면 뷰어의 `viewerFormats.ts` 와 같은 규율로 한 곳에만 적는다. 목록·버튼·안내가 전부 여기서
 * 나오므로, "열린다고 적혀 있는데 눌러도 안 열리는" 상태가 만들어지지 않는다.
 *
 * <h2>편집 가능 여부가 왜 확장자에 붙나</h2>
 * ONLYOFFICE 는 OOXML/ODF 만 <b>저장</b>할 수 있다. 옛 이진 형식(xls·doc·ppt)은 열어서 볼 수는 있지만
 * 그 형식 그대로 저장하지 못한다 — 그래서 처음부터 보기 전용으로 연다. 저장을 허용해 놓고 저장할 때
 * 실패하는 것이 사용자에게 가장 나쁘다(고친 것이 그 순간 사라진다).
 */

export type OfficeKind = 'cell' | 'word' | 'slide';

export type OfficeFormat = {
  kind: OfficeKind;
  /** 이 형식 그대로 저장할 수 있는가. false 면 열되 보기 전용이다. */
  editable: boolean;
};

/**
 * 문서 편집기가 여는 형식 — <b>이 배열이 정본</b>이다. 판정({@link officeFormatOf})도, 화면 안내
 * (`formatCatalog`)도 여기서 나온다. 형식을 더하려면 여기 한 줄만 더한다.
 */
export type OfficeFormatGroup = {
  kind: OfficeKind;
  /** 사용자에게 보이는 묶음 이름. */
  label: string;
  extensions: string[];
  editable: boolean;
  /** 이 묶음이 무엇인지 한 줄. 안내 화면에 그대로 나간다. */
  note: string;
};

export const OFFICE_FORMATS: OfficeFormatGroup[] = [
  {
    kind: 'cell',
    label: '표 (엑셀)',
    extensions: ['xlsx', 'xlsm', 'ods', 'csv'],
    editable: true,
    note: '수식·서식·차트가 그대로 열립니다. 여럿이 동시에 고칠 수 있습니다.',
  },
  {
    kind: 'word',
    label: '문서 (워드)',
    extensions: ['docx', 'odt', 'rtf', 'txt'],
    editable: true,
    note: '조판·표·이미지가 그대로 열립니다.',
  },
  {
    kind: 'slide',
    label: '슬라이드 (PPT)',
    extensions: ['pptx', 'odp'],
    editable: true,
    note: '장표를 넘겨 보고 고칠 수 있습니다.',
  },
  {
    kind: 'cell',
    label: '옛 오피스 형식',
    extensions: ['xls', 'doc', 'ppt'],
    editable: false,
    note: '열어서 볼 수는 있지만 그 형식 그대로 저장할 수 없습니다 — 고치려면 xlsx·docx·pptx 로 다시 저장해 올리세요.',
  },
];

const BY_EXTENSION: Record<string, OfficeFormat> = Object.fromEntries(
  OFFICE_FORMATS.flatMap((group) =>
    group.extensions.map((extension) => [extension, { kind: group.kind, editable: group.editable }]),
  ),
);

/** 소문자 확장자(점 없음). 확장자가 없으면 빈 문자열. */
export function officeExtensionOf(name: string): string {
  const file = name.split(/[\\/]/).pop() ?? '';
  const dot = file.lastIndexOf('.');
  return dot < 0 ? '' : file.slice(dot + 1).toLowerCase();
}

/** 파일 이름 → 형식. 문서 편집기가 여는 확장자가 아니면 null. */
export function officeFormatOf(name: string): OfficeFormat | null {
  return BY_EXTENSION[officeExtensionOf(name)] ?? null;
}

/**
 * 문서서버 없이 <b>브라우저 안에서 그릴 수 있는가</b>, 그리고 무엇으로 그리는가.
 *
 * <ul>
 *   <li>{@code 'sheet'} — xlsx 계열. `read-excel-file` 이 값을 읽어 표로 그린다(서식·차트는 없다).</li>
 *   <li>{@code 'text'} — 글자만 든 파일. 받아서 그대로 보여 준다. <b>서버가 필요 없다.</b></li>
 *   <li>{@code null} — docx·pptx 계열. 조판·도형을 우리가 재현할 수 없어 문서서버가 있어야 한다.</li>
 * </ul>
 *
 * <p>글자 파일을 빼먹었다가 지적받았다(2026-08-18): 문서서버가 없는 배포에서 <b>.txt 조차</b>
 * "받아서 여세요" 였다. 브라우저가 그냥 보여 줄 수 있는 것을 못 보여 주는 것은 설명되지 않는다.
 */
export type PreviewKind = 'sheet' | 'text';

const TEXT_EXTENSIONS = new Set(['txt', 'md', 'csv', 'log', 'json', 'xml', 'yml', 'yaml', 'ini', 'sql']);

export function previewKindOf(name: string): PreviewKind | null {
  const extension = officeExtensionOf(name);
  if (extension === 'xlsx' || extension === 'xlsm') return 'sheet';
  if (TEXT_EXTENSIONS.has(extension)) return 'text';
  return null;
}

/** 경로에서 표시용 파일 이름만. */
export function fileNameOf(ref: string): string {
  const trimmed = ref.endsWith('/') ? ref.slice(0, -1) : ref;
  return trimmed.split(/[\\/]/).pop() || trimmed;
}
